import { Children, Fragment, forwardRef } from 'react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import {
    OverflowEllipsis,
    PlaceholderShimmerBackground,
    UnstyledButton,
} from 'utils/fragments';
import styled, { css } from 'styled-components';
import Link from '../Link';

export const Breadcrumb = styled.span`
    height: 40px;
    border: solid 1px ${({ theme }) => theme.gray_5};
    border-radius: ${({ theme }) => theme.borderRadius_lg};
    padding: ${({ theme }) => theme.space_5}px ${({ theme }) => theme.space_8}px;
    font-family: ${({ theme }) => theme.style_primary};
    font-size: ${({ theme }) => theme.fontSize_base};
    font-weight: ${({ theme }) => theme.fontSemiBold};
    color: ${({ theme }) => theme.gray_3};
    display: inline-flex;
    align-items: center;
    justify-content: space-between;
    background-color: ${({ theme, isLoading }) =>
        isLoading ? theme.gray_5 : 'transparent'};
    opacity: ${({ isDisabled, isLoading }) =>
        isDisabled && !isLoading ? 0.5 : 1};
    ${({ isLoading }) => isLoading && PlaceholderShimmerBackground}
`;

const BaseHoverStyles = css`
    background-color: ${({ theme }) => theme.purple_5};
    color: ${({ theme }) => theme.purple_1};
    border-color: ${({ theme }) => theme.purple_4};
`;

const HoverStyles = css`
    &:hover,
    &:focus-visible {
        ${BaseHoverStyles}
    }

    ${({ open }) => open && BaseHoverStyles}
`;

const TruncateOverflow = styled.span`
    ${OverflowEllipsis}
`;

const UnselectedBreadcrumbWrapper = styled(Breadcrumb)`
    ${({ isDisabled, isLoading }) =>
        (isDisabled || isLoading) &&
        `cursor: ${!isLoading ? 'not-allowed' : 'wait'};`};
    ${({ isDisabled }) => !isDisabled && HoverStyles}
`;

const UnselectedBreadcrumbRightIcon = styled(FiChevronDown)`
    color: ${({ theme }) => theme.purple_1};
    font-size: 1.3125em;
    flex-shrink: 0;
`;

export const UnselectedBreadcrumb = forwardRef(
    ({ children, as = UnstyledButton, ...props }, ref) => (
        <UnselectedBreadcrumbWrapper as={as} {...props} ref={ref}>
            <TruncateOverflow>{children}</TruncateOverflow>
            <UnselectedBreadcrumbRightIcon />
        </UnselectedBreadcrumbWrapper>
    ),
);

export const SelectedBreadcrumbSelectOnly = forwardRef(
    ({ children, as = UnstyledButton, ...props }, ref) => (
        <SelectedBreadcrumbSelectOnlyWrapper as={as} {...props} ref={ref}>
            <TruncateOverflow>{children}</TruncateOverflow>
            <UnselectedBreadcrumbRightIcon />
        </SelectedBreadcrumbSelectOnlyWrapper>
    ),
);

export const SelectedBreadcrumb = styled(Breadcrumb)`
    align-items: stretch;
    padding: 0;
    box-shadow: 0px 2px 3px rgba(0, 0, 0, 0.07);
    background-color: ${({ theme }) => theme.gray_7};
    border-width: 0px;
    color: ${({ theme, isActive }) =>
        isActive ? theme.purple_1 : theme.gray_1};
    font-weight: ${({ theme }) => theme.fontBold};
    text-transform: capitalize;
`;

const SelectedBreadcrumbLinkWrapper = styled(Link)`
    padding: ${({ theme }) => theme.space_5}px ${({ theme }) => theme.space_8}px;
    display: inline-flex;
    align-items: center;
    flex-grow: 1;
    color: inherit;
    text-decoration: none;
    border: solid 1px ${({ theme }) => theme.gray_7};
    border-radius: ${({ theme }) =>
        `${theme.borderRadius_lg} 0px 0px ${theme.borderRadius_lg}`};
    ${({ isDisabled }) => !isDisabled && HoverStyles}
    overflow: hidden;
`;

export function SelectedBreadcrumbLink({ children, ...props }) {
    return (
        <SelectedBreadcrumbLinkWrapper {...props}>
            <TruncateOverflow>{children}</TruncateOverflow>
        </SelectedBreadcrumbLinkWrapper>
    );
}

const SelectedBreadcrumbPopOverTriggerWrapperHoverStyles = css`
    &:hover {
        background-color: ${({ theme }) => theme.purple_5};
        color: ${({ theme }) => theme.purple_1};
        border-color: ${({ theme }) => theme.purple_4};
    }
`;

const SelectedBreadcrumbPopOverTriggerWrapper = styled(UnstyledButton)`
    padding: ${({ theme }) => theme.space_5}px;
    color: ${({ theme }) => theme.purple_1};
    font-size: 1.3125em;
    display: inline-flex;
    align-items: center;
    border-radius: ${({ theme }) =>
        `0px ${theme.borderRadius_lg} ${theme.borderRadius_lg} 0px`};
    border: solid 1px ${({ theme }) => theme.gray_7};
    border-left-color: ${({ theme }) => theme.gray_5};
    ${({ disabled, isDisabled }) =>
        !(isDisabled || disabled) &&
        SelectedBreadcrumbPopOverTriggerWrapperHoverStyles}
`;

const SelectedBreadcrumbSelectOnlyWrapper = styled(SelectedBreadcrumb)`
    padding: ${({ theme }) => theme.space_5}px;
    padding-left: ${({ theme }) => theme.space_8}px;
    color: ${({ theme }) => theme.purple_1};
    display: inline-flex;
    align-items: center;
    border-radius: ${({ theme }) => theme.borderRadius_lg};
    border: solid 1px ${({ theme }) => theme.gray_7};
    ${({ disabled, isDisabled }) =>
        !(isDisabled || disabled) &&
        SelectedBreadcrumbPopOverTriggerWrapperHoverStyles}
`;

export const SelectedBreadcrumbPopOverTrigger = forwardRef((props, ref) => {
    return (
        <SelectedBreadcrumbPopOverTriggerWrapper
            aria-label="Open menu"
            {...(props.isDisabled && { disabled: true })}
            {...props}
            ref={ref}
        >
            <FiChevronDown />
        </SelectedBreadcrumbPopOverTriggerWrapper>
    );
});

const BreadcrumbSeparator = styled(FiChevronRight)`
    color: ${({ theme }) => theme.gray_4};
    justify-self: center;
`;

const BreadcrumbsWrapper = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(0, 1fr) minmax(0px, 40px));
    align-items: center;
    min-height: 40px;
`;

const BreadcrumbsContainer = forwardRef(
    ({ children, Separator = BreadcrumbSeparator, ...props }, ref) => {
        return (
            <BreadcrumbsWrapper {...props} ref={ref}>
                {Children.toArray(children)
                    .filter((child) => child !== null)
                    .map((child, idx) => {
                        return (
                            <Fragment key={idx}>
                                {idx > 0 ? <Separator /> : null}
                                {child}
                            </Fragment>
                        );
                    })}
            </BreadcrumbsWrapper>
        );
    },
);

export function BreadcrumbsSkeleton({ size = 4, status }) {
    return (
        <BreadcrumbsContainer>
            {new Array(size).fill('').map((s, idx) => (
                <Breadcrumb isLoading={status === 'loading'} key={idx} />
            ))}
        </BreadcrumbsContainer>
    );
}

export class Breadcrumbs {
    static Container = BreadcrumbsContainer;

    static Breadcrumb = Breadcrumb;

    static Skeleton = BreadcrumbsSkeleton;
}
